// pages/DynamicEventView.tsx

import { useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { getIndustryTheme } from '@/config/industryThemes';
import { useGetTemplateQuery } from '@/store/features/templates/templatesAPI';
import { toast } from 'sonner';
import {
  Calendar,
  MapPin,
  Users,
  Ticket,
  Share2,
  Heart,
  ChevronLeft,
  Clock,
} from 'lucide-react';

// Import ALL industry templates
import { TechEventTemplate } from '@/components/industry-templates/TechEventTemplate';
import { MusicEventTemplate } from '@/components/industry-templates/MusicEventTemplate';
import { SportsEventTemplate } from '@/components/industry-templates/SportsEventTemplate';
import { HealthcareEventTemplate } from '@/components/industry-templates/HealthcareEventTemplate';
import { ArtEventTemplate } from '@/components/industry-templates/ArtEventTemplate';
import { BusinessEventTemplate } from '@/components/industry-templates/BusinessEventTemplate';
import { FoodEventTemplate } from '@/components/industry-templates/FoodEventTemplate';
import { EducationEventTemplate } from '@/components/industry-templates/EducationEventTemplate';
import { FashionEventTemplate } from '@/components/industry-templates/FashionEventTemplate';
import { GamingEventTemplate } from '@/components/industry-templates/GamingEventTemplate';
import { NetworkingEventTemplate } from '@/components/industry-templates/NetworkingEventTemplate';
import { CharityEventTemplate } from '@/components/industry-templates/CharityEventTemplate';
import { FilmEventTemplate } from '@/components/industry-templates/FilmEventTemplate';
import { RealEstateEventTemplate } from '@/components/industry-templates/RealEstateEventTemplate';
import { AutomotiveEventTemplate } from '@/components/industry-templates/AutomotiveEventTemplate';
import { WeddingEventTemplate } from '@/components/industry-templates/WeddingEventTemplate';
import { WellnessEventTemplate } from '@/components/industry-templates/WellnessEventTemplate';
import { ComedyEventTemplate } from '@/components/industry-templates/ComedyEventTemplate';
import { PhotographyEventTemplate } from '@/components/industry-templates/PhotographyEventTemplate';
import { StartupEventTemplate } from '@/components/industry-templates/StartupEventTemplate';
import { CryptoEventTemplate } from '@/components/industry-templates/CryptoEventTemplate';
import { AgricultureEventTemplate } from '@/components/industry-templates/AgricultureEventTemplate';
import { ManufacturingEventTemplate } from '@/components/industry-templates/ManufacturingEventTemplate';
import { TravelEventTemplate } from '@/components/industry-templates/TravelEventTemplate';
import { RetailEventTemplate } from '@/components/industry-templates/RetailEventTemplate';
import { BankingEventTemplate } from '@/components/industry-templates/BankingEventTemplate';
import { HospitalityEventTemplate } from '@/components/industry-templates/HospitalityEventTemplate';
import { CybersecurityEventTemplate } from '@/components/industry-templates/CybersecurityEventTemplate';
import { DataScienceEventTemplate } from '@/components/industry-templates/DataScienceEventTemplate';
import { CloudEventTemplate } from '@/components/industry-templates/CloudEventTemplate';
import { PharmaEventTemplate } from '@/components/industry-templates/PharmaEventTemplate';
import { RenewableEnergyEventTemplate } from '@/components/industry-templates/RenewableEnergyEventTemplate';
import { ElectricVehiclesEventTemplate } from '@/components/industry-templates/ElectricVehiclesEventTemplate';
import { MentalHealthEventTemplate } from '@/components/industry-templates/MentalHealthEventTemplate';
import { LiteratureEventTemplate } from '@/components/industry-templates/LiteratureEventTemplate';
import { InsuranceEventTemplate } from '@/components/industry-templates/InsuranceEventTemplate';
import { ConstructionEventTemplate } from '@/components/industry-templates/ConstructionEventTemplate';
import { LogisticsEventTemplate } from '@/components/industry-templates/LogisticsEventTemplate';
import { MediaEventTemplate } from '@/components/industry-templates/MediaEventTemplate';
import { TelecomEventTemplate } from '@/components/industry-templates/TelecomEventTemplate';
import { LegalEventTemplate } from '@/components/industry-templates/LegalEventTemplate';
import { ArchitectureEventTemplate } from '@/components/industry-templates/ArchitectureEventTemplate';
import { HREventTemplate } from '@/components/industry-templates/HREventTemplate';
import { AviationEventTemplate } from '@/components/industry-templates/AviationEventTemplate';
import { MaritimeEventTemplate } from '@/components/industry-templates/MaritimeEventTemplate';
import { MiningEventTemplate } from '@/components/industry-templates/MiningEventTemplate';
import { ChemicalsEventTemplate } from '@/components/industry-templates/ChemicalsEventTemplate';
import { BiotechEventTemplate } from '@/components/industry-templates/BiotechEventTemplate';
import { EnvironmentEventTemplate } from '@/components/industry-templates/EnvironmentEventTemplate';
import { InteriorDesignEventTemplate } from '@/components/industry-templates/InteriorDesignEventTemplate';
import { UniversalEventTemplate } from '@/components/industry-templates/UniversalEventTemplate';

const DynamicEventView = () => {
  const { temp_id } = useParams<{ temp_id: string }>();
  const navigate = useNavigate();

  const { data: tempdata, isLoading, isFetching, isError } = useGetTemplateQuery(temp_id!);
  
  const structure = tempdata?.data?.structure;
  const industry = tempdata?.data?.industry || 'tech';
  const theme = getIndustryTheme(industry);

  if (isLoading || isFetching) return <EventSkeleton />;
  
  if (isError || !structure) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
        <Card className="max-w-md border-red-500/50">
          <CardContent className="p-8 text-center space-y-4">
            <div className="text-6xl">❌</div>
            <h2 className="text-2xl font-bold text-white">Template Not Found</h2>
            <p className="text-slate-400">The template you're looking for doesn't exist</p>
            <Button onClick={() => navigate('/templates')}>Browse Templates</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: structure.title,
          text: structure.short_description,
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        toast.success('Link copied to clipboard!');
      }
    } catch (err) {
      console.error('Share failed:', err);
    }
  };

  // ✅ COMPLETE SWITCH STATEMENT FOR ALL 50 INDUSTRIES
  const renderIndustryTemplate = () => {
    const props = { structure, theme };

    switch (industry) {
      case 'tech': return <TechEventTemplate {...props} />;
      case 'music': return <MusicEventTemplate {...props} />;
      case 'sports': return <SportsEventTemplate {...props} />;
      case 'healthcare': return <HealthcareEventTemplate {...props} />;
      case 'art': return <ArtEventTemplate {...props} />;
      case 'business': return <BusinessEventTemplate {...props} />;
      case 'food': return <FoodEventTemplate {...props} />;
      case 'education': return <EducationEventTemplate {...props} />;
      case 'fashion': return <FashionEventTemplate {...props} />;
      case 'gaming': return <GamingEventTemplate {...props} />;
      case 'networking': return <NetworkingEventTemplate {...props} />;
      case 'charity': return <CharityEventTemplate {...props} />;
      case 'film': return <FilmEventTemplate {...props} />;
      case 'real-estate': return <RealEstateEventTemplate {...props} />;
      case 'automotive': return <AutomotiveEventTemplate {...props} />;
      case 'wedding': return <WeddingEventTemplate {...props} />;
      case 'wellness': return <WellnessEventTemplate {...props} />;
      case 'comedy': return <ComedyEventTemplate {...props} />;
      case 'photography': return <PhotographyEventTemplate {...props} />;
      case 'startup': return <StartupEventTemplate {...props} />;
      case 'crypto': return <CryptoEventTemplate {...props} />;
      case 'agriculture': return <AgricultureEventTemplate {...props} />;
      case 'manufacturing': return <ManufacturingEventTemplate {...props} />;
      case 'travel': return <TravelEventTemplate {...props} />;
      case 'insurance': return <InsuranceEventTemplate {...props} />;
      case 'construction': return <ConstructionEventTemplate {...props} />;
      case 'logistics': return <LogisticsEventTemplate {...props} />;
      case 'retail': return <RetailEventTemplate {...props} />;
      case 'banking': return <BankingEventTemplate {...props} />;
      case 'hospitality': return <HospitalityEventTemplate {...props} />;
      case 'media': return <MediaEventTemplate {...props} />;
      case 'telecom': return <TelecomEventTemplate {...props} />;
      case 'legal': return <LegalEventTemplate {...props} />;
      case 'architecture': return <ArchitectureEventTemplate {...props} />;
      case 'hr': return <HREventTemplate {...props} />;
      case 'electric-vehicles': return <ElectricVehiclesEventTemplate {...props} />;
      case 'aviation': return <AviationEventTemplate {...props} />;
      case 'maritime': return <MaritimeEventTemplate {...props} />;
      case 'mining': return <MiningEventTemplate {...props} />;
      case 'chemicals': return <ChemicalsEventTemplate {...props} />;
      case 'pharma': return <PharmaEventTemplate {...props} />;
      case 'biotech': return <BiotechEventTemplate {...props} />;
      case 'renewable-energy': return <RenewableEnergyEventTemplate {...props} />;
      case 'environment': return <EnvironmentEventTemplate {...props} />;
      case 'cybersecurity': return <CybersecurityEventTemplate {...props} />;
      case 'data-science': return <DataScienceEventTemplate {...props} />;
      case 'cloud': return <CloudEventTemplate {...props} />;
      case 'interior-design': return <InteriorDesignEventTemplate {...props} />;
      case 'mental-health': return <MentalHealthEventTemplate {...props} />;
      case 'literature': return <LiteratureEventTemplate {...props} />;
      default: return <UniversalEventTemplate {...props} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Dynamic Hero */}
      <div className="relative h-[500px] w-full overflow-hidden">
        <div className={`absolute inset-0 bg-gradient-to-br ${theme.gradient} opacity-90`} />
        
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />

        <div className="relative h-full flex items-end">
          <div className="container mx-auto px-6 pb-12">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="text-4xl">{theme.icon}</span>
              <Badge className={`${theme.badge} text-white border-0 px-4 py-1 text-sm capitalize`}>
                {industry.replace('-', ' ')}
              </Badge>
              {structure.featured && (
                <Badge className="bg-yellow-500 text-black border-0 px-4 py-1">⭐ Featured</Badge>
              )}
              <Badge variant="outline" className="border-white text-white px-4 py-1 capitalize">
                {structure.format?.replace('_', ' ')}
              </Badge>
              <Badge variant="outline" className="border-white text-white px-4 py-1 capitalize">
                {structure.event_type}
              </Badge>
            </div>

            <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 max-w-4xl">
              {structure.title}
            </h1>
            <p className="text-2xl text-white/90 mb-6">{structure.subtitle}</p>
            <p className="text-lg text-white/80">Organized by {structure.organizer_name}</p>
          </div>
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="absolute top-6 left-6 text-white hover:bg-white/20"
          onClick={() => navigate(-1)}
        >
          <ChevronLeft className="w-6 h-6" />
        </Button>

        <div className="absolute top-6 right-6 flex gap-2">
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20" onClick={handleShare}>
            <Share2 className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="icon" className="text-white hover:bg-white/20">
            <Heart className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto py-12 px-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Industry-Specific Content */}
          <div className="lg:col-span-2 space-y-8">
            {renderIndustryTemplate()}
          </div>

          {/* Right Column - Booking Card */}
          <div className="lg:col-span-1">
            <Card
              className="sticky top-4 border-2 backdrop-blur"
              style={{ borderColor: theme.primary, backgroundColor: 'rgba(15, 23, 42, 0.8)' }}
            >
              <CardContent className="p-6 space-y-6">
                <div>
                  <p className="text-3xl font-bold" style={{ color: theme.primary }}>
                    {structure.free_event ? (
                      'FREE'
                    ) : (
                      <>
                        {structure.currency === 'USD' ? '$' : '₹'}
                        {structure.base_price?.toLocaleString()}
                      </>
                    )}
                  </p>
                  <p className="text-sm text-slate-400">per ticket</p>
                </div>

                <div className="p-4 rounded-lg" style={{ backgroundColor: `${theme.primary}20` }}>
                  <div className="flex items-center gap-2" style={{ color: theme.primary }}>
                    <Ticket className="w-5 h-5" />
                    <span className="font-semibold">Tickets Available</span>
                  </div>
                  <p className="text-sm mt-1 text-slate-400">
                    {(structure.capacity || 0) - (structure.registered_count || 0)} of {structure.capacity} remaining
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between text-sm text-slate-300">
                    <span className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Registered
                    </span>
                    <span className="font-semibold">{structure.registered_count || 0}</span>
                  </div>
                  <div className="flex justify-between text-sm text-slate-300">
                    <span className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Capacity
                    </span>
                    <span className="font-semibold">{structure.capacity}</span>
                  </div>
                </div>

                <Button
                  className="w-full text-white font-bold text-lg h-14"
                  style={{ backgroundColor: theme.primary }}
                  onClick={() => toast.success('Booking functionality coming soon!')}
                >
                  <Ticket className="w-5 h-5 mr-2" />
                  Book Now
                </Button>

                <div className="text-xs text-center text-slate-400 space-y-1">
                  {structure.send_confirmations && <p>✓ Instant confirmation</p>}
                  <p>✓ E-ticket via email</p>
                  {structure.free_event && <p>✓ No payment required</p>}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

// Generic Template Fallback
const GenericTemplate = ({ structure, theme }: any) => (
  <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
    <CardContent className="p-6">
      <p className="text-slate-300 leading-relaxed">{structure.description}</p>
    </CardContent>
  </Card>
);

// Skeleton Component
const EventSkeleton = () => (
  <div className="min-h-screen bg-slate-950 animate-pulse">
    <div className="h-[500px] bg-slate-800" />
    <div className="container mx-auto py-12 px-6">
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-2 space-y-6">
          <div className="h-32 bg-slate-800 rounded" />
          <div className="h-64 bg-slate-800 rounded" />
        </div>
        <div className="h-96 bg-slate-800 rounded" />
      </div>
    </div>
  </div>
);

export default DynamicEventView;
